import { FormattedMessage, FormattedNumber } from 'react-intl';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import usePlans from '@alltrails/context/hooks/usePlans';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import PurchaseTier from '@alltrails/core/types/PurchaseTier';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments } from '@alltrails/experiments';

const TierSubheading = ({ tier }: { tier: PurchaseTier }) => {
  const currencyCode = useCurrencyCode();
  const plans = usePlans();
  const plan = plans[tier];
  const user = useUser();
  const isMobileSizedScreen = useIsMobileSizedScreen();
  const experiments = useExperiments();
  const is14DayTrialEnabled = experiments['web-growth-rm-14-day-trial']?.value === 'on';

  const isPromoEligible = user?.isPromoEligible;

  function getText() {
    if (isPromoEligible) {
      if (is14DayTrialEnabled) {
        return isMobileSizedScreen ? (
          <FormattedMessage
            defaultMessage="14 days free, then {annualCost}/year ({monthlyCost}/month)"
            values={{
              annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
              monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
            }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="14 days free, then {annualCost}/year (that’s {monthlyCost}/month)"
            values={{
              annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
              monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
            }}
          />
        );
      }

      return isMobileSizedScreen ? (
        <FormattedMessage
          defaultMessage="7 days free, then {annualCost}/year ({monthlyCost}/month)"
          values={{
            annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
            monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
          }}
        />
      ) : (
        <FormattedMessage
          defaultMessage="7 days free, then {annualCost}/year (that’s {monthlyCost}/month)"
          values={{
            annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
            monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
          }}
        />
      );
    }

    if (user?.canUpgradeExistingSubscription && !user?.subscription?.trial) {
      return (
        <FormattedMessage
          defaultMessage="{annualCost}/year, and we’ll <b>refund</b> the rest of your current plan"
          values={{
            annualCost: (
              <b>
                <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />
              </b>
            ),
            b: chunks => <b>{chunks}</b>
          }}
        />
      );
    }

    return (
      <FormattedMessage
        defaultMessage="{annualCost}/year (that’s {monthlyCost}/month)"
        values={{
          annualCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceTotal} />,
          monthlyCost: <FormattedNumber currency={currencyCode} style="currency" value={plan.priceMonth} />
        }}
      />
    );
  }

  return (
    <Typography component="div" variant="text200">
      {getText()}
    </Typography>
  );
};

export default TierSubheading;
